"use client";

import { useState, useCallback, useRef } from "react";
import { Droplets, CheckCircle2, Loader2, ExternalLink, AlertCircle, Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePasskey } from "@/contexts/PasskeyContext";
import {
  TESTNET_TOKENS,
  POOL_ADDRESSES,
  TOKEN_UNIT,
  getTxExplorerUrl,
} from "@/lib/hoops-contracts";
import { recordActivity } from "@/services/activity.service";

// ---------------------------------------------------------------------------
// FriendbotFund
//
// Two-step "Get Testnet USDC" flow:
//   Step 1 — Request ~9,900 XLM from Stellar Friendbot (no signature needed)
//   Step 2 — Swap ~4,900 XLM → USDC via Soroswap (requires passkey signature)
//
// After completion the user has roughly 5,000 XLM (gas reserve) and a USDC
// balance ready to deposit into a strategy.
// ---------------------------------------------------------------------------

type StepStatus = "idle" | "running" | "done" | "error";

interface StepState {
  status: StepStatus;
  txHash: string | null;
  errorMessage: string | null;
}

type FlowPhase = "idle" | "step1" | "step2" | "complete" | "error";

interface Props {
  /** Called after both steps succeed so the parent can refresh balances. */
  onSuccess?: () => void;
}

const INITIAL_STEP: StepState = { status: "idle", txHash: null, errorMessage: null };

export function FriendbotFund({ onSuccess }: Props) {
  const { fundWallet, swap, isConnected } = usePasskey();
  const [phase, setPhase] = useState<FlowPhase>("idle");
  const [step1, setStep1] = useState<StepState>(INITIAL_STEP);
  const [step2, setStep2] = useState<StepState>(INITIAL_STEP);
  const busyRef = useRef(false);

  const reset = useCallback(() => {
    setPhase("idle");
    setStep1(INITIAL_STEP);
    setStep2(INITIAL_STEP);
  }, []);

  const handleGetUSDC = useCallback(async () => {
    if (busyRef.current || !isConnected) return;
    busyRef.current = true;

    // Reset from any prior attempt
    setStep1(INITIAL_STEP);
    setStep2(INITIAL_STEP);

    // -----------------------------------------------------------------------
    // Step 1: Fund XLM via Friendbot
    // -----------------------------------------------------------------------
    setPhase("step1");
    setStep1({ status: "running", txHash: null, errorMessage: null });

    let fundHash: string | null = null;

    try {
      const result = await fundWallet(TESTNET_TOKENS.XLM);

      if (!result?.success) {
        const msg = result?.error ?? "Friendbot request failed. Please try again.";
        setStep1({ status: "error", txHash: null, errorMessage: msg });
        setPhase("error");
        busyRef.current = false;
        return;
      }

      fundHash = result.hash;
      setStep1({ status: "done", txHash: fundHash, errorMessage: null });
      void recordActivity({
        txHash: fundHash,
        action: "fund",
        status: "success",
        amounts: { xlm: 9_900 },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Funding failed. Please try again.";
      setStep1({ status: "error", txHash: null, errorMessage: msg });
      setPhase("error");
      busyRef.current = false;
      return;
    }

    // -----------------------------------------------------------------------
    // Step 2: Swap ~4,900 XLM → USDC via Soroswap (passkey required)
    // -----------------------------------------------------------------------
    setPhase("step2");
    setStep2({ status: "running", txHash: null, errorMessage: null });

    try {
      const xlmToSwap = 4_900n * BigInt(TOKEN_UNIT);
      const deadline = Math.floor(Date.now() / 1000) + 600;

      const result = await swap(
        TESTNET_TOKENS.XLM,
        TESTNET_TOKENS.USDC,
        xlmToSwap,
        POOL_ADDRESSES.SOROSWAP_PAIR,
        deadline,
      );

      if (!result?.success) {
        const msg = result?.error ?? "Swap failed. You can retry from this step.";
        setStep2({ status: "error", txHash: null, errorMessage: msg });
        setPhase("error");
        busyRef.current = false;
        return;
      }

      setStep2({ status: "done", txHash: result.hash, errorMessage: null });
      setPhase("complete");
      void recordActivity({
        txHash: result.hash,
        action: "swap",
        status: "success",
      });
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Swap failed. Please try again.";
      setStep2({ status: "error", txHash: null, errorMessage: msg });
      setPhase("error");
    } finally {
      busyRef.current = false;
    }
  }, [fundWallet, swap, isConnected, onSuccess]);

  // If step 1 succeeded but step 2 failed, allow retrying only step 2
  const handleRetrySwap = useCallback(async () => {
    if (busyRef.current || !isConnected) return;
    busyRef.current = true;

    setPhase("step2");
    setStep2({ status: "running", txHash: null, errorMessage: null });

    try {
      const xlmToSwap = 4_900n * BigInt(TOKEN_UNIT);
      const deadline = Math.floor(Date.now() / 1000) + 600;

      const result = await swap(
        TESTNET_TOKENS.XLM,
        TESTNET_TOKENS.USDC,
        xlmToSwap,
        POOL_ADDRESSES.SOROSWAP_PAIR,
        deadline,
      );

      if (!result?.success) {
        const msg = result?.error ?? "Swap failed. Please try again.";
        setStep2({ status: "error", txHash: null, errorMessage: msg });
        setPhase("error");
        busyRef.current = false;
        return;
      }

      setStep2({ status: "done", txHash: result.hash, errorMessage: null });
      setPhase("complete");
      void recordActivity({
        txHash: result.hash,
        action: "swap",
        status: "success",
      });
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Swap failed. Please try again.";
      setStep2({ status: "error", txHash: null, errorMessage: msg });
      setPhase("error");
    } finally {
      busyRef.current = false;
    }
  }, [swap, isConnected, onSuccess]);

  const isRunning = phase === "step1" || phase === "step2";
  const step1Succeeded = step1.status === "done";
  const step2Failed = step2.status === "error";

  return (
    <section aria-label="Get testnet USDC">
      <div
        className="px-4 py-3.5 flex flex-col gap-3"
        style={{ borderLeft: "2px solid #1e3a5f" }}
      >
        {/* Header row */}
        <div className="flex items-center gap-2.5">
          <Droplets className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#4a7fb5" }} strokeWidth={1.5} aria-hidden="true" />
          <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
            <p
              className="text-[12px] font-medium leading-none"
              style={{ color: "var(--text-primary)" }}
            >
              Get Testnet USDC
            </p>
            <p
              className="text-[12px] leading-none"
              style={{ color: "var(--text-secondary)" }}
            >
              XLM from Friendbot, USDC via Soroswap swap.
            </p>
            <span
              style={{ fontSize: 9, color: "#333", letterSpacing: "0.02em" }}
            >
              Testnet only
            </span>
          </div>
        </div>

        {/* Progress steps — visible once flow has started */}
        {phase !== "idle" && (
          <div
            className="flex flex-col gap-0 rounded overflow-hidden"
            style={{ border: "1px solid var(--border-subtle)" }}
            role="status"
            aria-live="polite"
            aria-label="Funding progress"
          >
            {/* Step 1 row */}
            <StepRow
              stepNumber={1}
              label="Requesting testnet XLM from Stellar Friendbot"
              sublabel={null}
              status={step1.status}
              txHash={step1.txHash}
              successLabel="Received ~9,900 XLM"
              errorMessage={step1.errorMessage}
              isLast={false}
            />

            {/* Divider */}
            <div style={{ height: "1px", background: "var(--border-subtle)" }} aria-hidden="true" />

            {/* Step 2 row */}
            <StepRow
              stepNumber={2}
              label="Swapping ~4,900 XLM \u2192 USDC via Soroswap"
              sublabel={
                step2.status === "running"
                  ? "Biometric prompt will appear — your passkey signs this swap"
                  : step2.status === "idle" && phase === "step2"
                  ? "Your passkey will be used to sign the swap"
                  : null
              }
              status={step2.status}
              txHash={step2.txHash}
              successLabel="Swapped! You now have USDC ready to deposit."
              errorMessage={step2.errorMessage}
              isLast={true}
            />
          </div>
        )}

        {/* Completion callout */}
        {phase === "complete" && (
          <div
            className="rounded px-3 py-2 flex items-start gap-2"
            style={{
              background: "rgba(62, 207, 126, 0.07)",
              border: "1px solid rgba(62, 207, 126, 0.18)",
            }}
          >
            <CheckCircle2
              className="h-3.5 w-3.5 flex-shrink-0 mt-px"
              style={{ color: "var(--accent)" }}
              aria-hidden="true"
            />
            <p className="text-[11px] leading-relaxed" style={{ color: "var(--accent)" }}>
              You now have ~5,000 XLM and USDC. Ready to deposit into a strategy.
            </p>
          </div>
        )}

        {/* Action area */}
        <div className="flex items-center gap-3">
          {/* Button — shown when not complete */}
          {phase !== "complete" && (
            <>
              {/* Full retry (phase is idle or step 1 failed) */}
              {(!step1Succeeded || phase === "idle") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start px-3 h-7 text-[11px]"
                  style={{
                    background: "#141414",
                    border: "1px solid #222",
                    color: "#888",
                  }}
                  disabled={isRunning || !isConnected}
                  onClick={() => void handleGetUSDC()}
                  aria-label={
                    phase === "idle"
                      ? "Start two-step testnet USDC funding"
                      : "Retry funding from the beginning"
                  }
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                      {phase === "step1" ? "Requesting XLM..." : "Swapping XLM..."}
                    </>
                  ) : phase === "error" && !step1Succeeded ? (
                    "Retry"
                  ) : (
                    "Get Testnet USDC"
                  )}
                </Button>
              )}

              {/* Partial retry — step 1 succeeded but step 2 failed */}
              {step1Succeeded && step2Failed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start px-3 h-7 text-[11px]"
                  style={{
                    background: "#141414",
                    border: "1px solid #222",
                    color: "#888",
                  }}
                  disabled={isRunning || !isConnected}
                  onClick={() => void handleRetrySwap()}
                  aria-label="Retry the XLM to USDC swap"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                      Swapping XLM...
                    </>
                  ) : (
                    <>
                      <Fingerprint className="h-3 w-3" aria-hidden="true" />
                      Retry Swap
                    </>
                  )}
                </Button>
              )}

              {/* "Start over" escape hatch when step 1 failed */}
              {phase === "error" && !step1Succeeded && (
                <button
                  type="button"
                  className="text-[11px] transition-opacity hover:opacity-80"
                  style={{ color: "var(--text-secondary)" }}
                  onClick={reset}
                  aria-label="Reset and start over"
                >
                  Start over
                </button>
              )}
            </>
          )}

          {/* Passkey hint shown before step 2 runs, while idle */}
          {phase === "idle" && (
            <span
              className="flex items-center gap-1.5 text-[11px]"
              style={{ color: "var(--text-secondary)" }}
              aria-hidden="true"
            >
              <Fingerprint className="h-3 w-3 flex-shrink-0" />
              Step 2 requires your passkey
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// StepRow — a single progress row inside the steps panel
// ---------------------------------------------------------------------------

interface StepRowProps {
  stepNumber: number;
  label: string;
  sublabel: string | null;
  status: StepStatus;
  txHash: string | null;
  successLabel: string;
  errorMessage: string | null;
  isLast: boolean;
}

function StepRow({
  stepNumber,
  label,
  sublabel,
  status,
  txHash,
  successLabel,
  errorMessage,
  isLast: _isLast,
}: StepRowProps) {
  const isIdle = status === "idle";
  const isRunning = status === "running";
  const isDone = status === "done";
  const isError = status === "error";

  return (
    <div
      className="flex items-start gap-2.5 px-3 py-2.5"
      style={{
        background: isRunning
          ? "rgba(96, 165, 250, 0.04)"
          : isDone
          ? "rgba(62, 207, 126, 0.04)"
          : isError
          ? "rgba(224, 82, 82, 0.04)"
          : "transparent",
      }}
    >
      {/* Step indicator */}
      <div className="flex-shrink-0 mt-0.5" aria-hidden="true">
        {isDone ? (
          <CheckCircle2 className="h-4 w-4" style={{ color: "var(--accent)" }} />
        ) : isRunning ? (
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#60a5fa" }} />
        ) : isError ? (
          <AlertCircle className="h-4 w-4" style={{ color: "#e05252" }} />
        ) : (
          // Idle — numbered circle
          <span
            className="h-4 w-4 rounded-full flex items-center justify-center text-[10px] font-medium leading-none"
            style={{
              background: "var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            {stepNumber}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex flex-col gap-0.5">
        {/* Main label — dim when idle */}
        <p
          className="text-[12px] leading-snug"
          style={{
            color: isDone
              ? "var(--accent)"
              : isError
              ? "#e05252"
              : isIdle
              ? "var(--text-secondary)"
              : "var(--text-primary)",
          }}
        >
          {isDone ? successLabel : label}
        </p>

        {/* Sublabel (passkey warning, etc.) */}
        {sublabel && !isDone && !isError && (
          <p
            className="text-[11px] leading-relaxed flex items-center gap-1"
            style={{ color: "var(--text-secondary)" }}
          >
            <Fingerprint className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            {sublabel}
          </p>
        )}

        {/* Error message */}
        {isError && errorMessage && (
          <p
            className="text-[11px] leading-relaxed"
            style={{ color: "#e05252" }}
            role="alert"
          >
            {errorMessage}
          </p>
        )}

        {/* TX explorer link */}
        {isDone && txHash && (
          <a
            href={getTxExplorerUrl(txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] transition-opacity hover:opacity-80 w-fit"
            style={{ color: "var(--text-secondary)" }}
            aria-label={`View step ${String(stepNumber)} transaction on Stellar Explorer`}
          >
            View transaction
            <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}
